/**
 * The assembler's true-up as both platforms show it: a dialog that opens
 * working, shows what the true-up changed once it is done, and stays until
 * dismissed. A true-up holds the thread while it runs, and that can take a
 * moment on a slow device, so the dialog shows its working state first and
 * the true-up starts only once the screen has had time to draw it. Both
 * platforms draw the dialog from this hook and word it with
 * `TRUE_UP_DIALOG_TEXT`.
 */
import { useEffect, useState } from 'react';
import { TRUE_UP_MAX_ADJUSTMENT, trueUpChanges, trueUpSummary } from '../assembler';
import type { MeasurementUnits } from '../editor-model';
import { useFloorPlanAssemblerState } from './useFloorPlanAssemblerState';

/** The dialog's words, the same on both platforms. */
export const TRUE_UP_DIALOG_TEXT = {
  title: 'True up measurements',
  working: `Lining up walls, doorways and rooms. No measured value changes by more than ${TRUE_UP_MAX_ADJUSTMENT * 12} inches. This can take a moment.`,
  dismiss: 'Done',
} as const;

/**
 * How long the working dialog shows before the true-up starts (ms): past a
 * frame or two on any screen, so it has drawn before the true-up holds the
 * thread. A timer rather than an animation frame, which runs before the next
 * draw, and the same on the web and React Native.
 */
const DRAW_BEFORE_TRUE_UP_MS = 50;

/** Where the dialog is: closed, working, or done with what the true-up did. */
export type TrueUpDialogState =
  | { phase: 'closed' }
  | { phase: 'working' }
  | { phase: 'done'; summary: string; changes: string[] };

/** The dialog's state, with `start` (open it working and true up the level once it has drawn) and `dismiss` (close it). */
export type TrueUpDialog = TrueUpDialogState & {
  start: () => void;
  dismiss: () => void;
};

const CLOSED: TrueUpDialogState = { phase: 'closed' };
const WORKING: TrueUpDialogState = { phase: 'working' };

/**
 * The level's true-up as a dialog, its report in `units`. Starting it again
 * while it works runs the true-up once; with nothing it may true up (a
 * read-only plan) it closes again.
 */
export function useTrueUpDialog(units: MeasurementUnits): TrueUpDialog {
  const { trueUp } = useFloorPlanAssemblerState();
  const [state, setState] = useState<TrueUpDialogState>(CLOSED);
  const working = state.phase === 'working';
  useEffect(() => {
    if (!working) return;
    const timer = setTimeout(() => {
      const result = trueUp();
      setState(result ? { phase: 'done', summary: trueUpSummary(result, units), changes: trueUpChanges(result, units) } : CLOSED);
    }, DRAW_BEFORE_TRUE_UP_MS);
    return () => clearTimeout(timer);
  }, [working, trueUp, units]);
  return { ...state, start: () => setState(WORKING), dismiss: () => setState(CLOSED) };
}
